import React, { useEffect, useState } from 'react';
import { ArrowLeft, ShieldCheck, AlertCircle, Sparkles, MessageCircle, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
interface OTPScreenProps {
  phoneNumber: string;
  initialDeliveryMethod: 'whatsapp' | 'call';
  onBack: () => void;
  onVerify: () => void;
}
export function OTPScreen({
  phoneNumber,
  initialDeliveryMethod,
  onBack,
  onVerify
}: OTPScreenProps) {
  const {
    isLight
  } = useTheme();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState<'whatsapp' | 'call'>(initialDeliveryMethod);
  const [countdown, setCountdown] = useState(120);
  const [error, setError] = useState('');
  const [shakeKey, setShakeKey] = useState(0);
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);
  useEffect(() => {
    setDeliveryMethod(initialDeliveryMethod);
    setCountdown(120);
    setError('');
    setOtp(['', '', '', '', '', '']);
  }, [initialDeliveryMethod]);
  const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const handleVerify = () => {
    setIsLoading(true);
    setError('');
    setTimeout(() => {
      setIsLoading(false);
      onVerify();
    }, 1500);
  };
  const switchDeliveryMethod = (method: 'whatsapp' | 'call') => {
    setDeliveryMethod(method);
    setCountdown(120);
    setError('');
    setOtp(['', '', '', '', '', '']);
    setTimeout(() => document.getElementById('otp-0')?.focus(), 100);
  };
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');
    // Auto-advance
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
    // Auto-submit when full
    if (value && index === 5 && newOtp.every(d => d)) {
      setTimeout(() => {
        setIsLoading(true);
        setError('');
        setTimeout(() => {
          setIsLoading(false);
          onVerify();
        }, 1500);
      }, 300);
    }
  };
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };
  return <div className="h-full flex flex-col p-6 relative overflow-hidden" data-id="element-3377" data-mp="kls8l7t7dsl0">
      {/* Background orbs */}
      <div className={`absolute top-20 -right-16 w-48 h-48 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/15' : 'bg-vexu/10'}`} data-id="element-3378" data-mp="2hgoc40yynpm1" />

      {/* Header */}
      <div className="flex items-center mb-8 relative z-10" data-id="element-3379" data-mp="13jlmfhjlt1hr">
        <button onClick={onBack} className={`p-2 -ml-2 rounded-full border transition-colors ${isLight ? 'text-slate-500 hover:text-slate-900 bg-white/60 backdrop-blur-md border-slate-200' : 'text-zinc-400 hover:text-white bg-white/5 backdrop-blur-md border-white/10'}`} data-id="element-3380" data-mp="yrk5hibhqmle">
          <ArrowLeft className="w-5 h-5" data-id="element-3381" data-mp="1mi6b72y0vus8" />
        </button>
      </div>

      <div className="flex-1 flex flex-col pt-8 relative z-10" data-id="element-3382" data-mp="1mfbsbfunqi0x">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} data-id="element-3383" data-mp="1igktn4ajmsbp">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-8 ${isLight ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20' : 'bg-white text-black shadow-lg shadow-white/10'}`} data-id="element-3384" data-mp="22umfoo4nnxu">
            <ShieldCheck className="w-7 h-7" data-id="element-3385" data-mp="1ky4suc6xt17e" />
          </div>

          <h1 className={`text-3xl font-bold mb-4 tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3386" data-mp="1pqegtmohrr71">
            {deliveryMethod === 'call' ? 'We’ll call you with a code' : 'Verify your number'}
          </h1>
          <p className={`text-base leading-relaxed mb-4 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3387" data-mp="24nomgfege2uu">
            {deliveryMethod === 'call' ? 'Answer the call at ' : 'Enter the 6-digit code we sent to your WhatsApp at '}
            <span className={`font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3388" data-mp="1urlkb9jlq47w">
              +1 {phoneNumber}
            </span>
            {deliveryMethod === 'call' && ' and enter the 6-digit code spoken to you.'}
          </p>

          <div className={`inline-flex items-center gap-1.5 self-start mb-4 px-3 py-1.5 rounded-full border ${deliveryMethod === 'call' ? isLight ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-blue-500/10 border-blue-500/20 text-blue-300' : isLight ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'}`} data-id="element-3389" data-mp="oagiwhh14eyx">
            {deliveryMethod === 'call' ? <Phone className="w-3.5 h-3.5" data-id="element-3390" data-mp="3t6jc426ew8vc" /> : <MessageCircle className="w-3.5 h-3.5" data-id="element-3391" data-mp="3t4yeu4spqbts" />}
            <span className="text-xs font-semibold" data-id="element-3392" data-mp="3p6r4broue6r1">
              {deliveryMethod === 'call' ? 'Code via phone call' : 'Sent via WhatsApp'}
            </span>
          </div>

          <AnimatePresence mode="wait" data-id="element-3393" data-mp="1z6x9tt047il">
            {deliveryMethod === 'call' && <motion.div initial={{
            opacity: 0,
            y: -6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} className={`flex items-start gap-2.5 mb-6 px-3.5 py-3 rounded-xl ${isLight ? 'bg-blue-50 border border-blue-100' : 'bg-blue-500/10 border border-blue-500/15'}`} data-id="element-3394" data-mp="3af8nxss2ma75">
                <Phone className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isLight ? 'text-blue-600' : 'text-blue-300'}`} data-id="element-3395" data-mp="1o2f1m30ds95l" />
                <p className={`text-xs leading-relaxed ${isLight ? 'text-blue-800' : 'text-blue-100/90'}`} data-id="element-3396" data-mp="ve14rql13ivm">
                  We’ll call +1 {phoneNumber} with your 6-digit code. Answer the
                  call, then enter the code here manually.
                </p>
              </motion.div>}
          </AnimatePresence>

          <div className="w-full space-y-8" data-id="element-3397" data-mp="2bv5ag3czmcbn">
            <div className="w-full space-y-8" data-id="element-3398" data-mp="2ftcub72qztr2">
              <motion.div key={shakeKey} animate={shakeKey > 0 ? {
              x: [0, -12, 12, -8, 8, -4, 4, 0]
            } : {}} transition={{
              duration: 0.5,
              ease: 'easeInOut'
            }} className="flex justify-between gap-2" data-id="element-3399" data-mp="2fw8rgt6rueeg">
                {otp.map((digit, i) => <input key={i} id={`otp-${i}`} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleKeyDown(i, e)} autoFocus={i === 0} className={`w-12 h-14 text-center text-2xl font-bold border-2 rounded-2xl outline-none transition-all duration-300 ${error ? isLight ? 'bg-red-50 border-red-300 text-red-600 focus:border-red-500 focus:bg-white' : 'bg-red-500/5 border-red-500/30 text-red-400 focus:border-red-500 focus:bg-white/5' : isLight ? 'bg-white border-slate-200 text-slate-900 focus:border-slate-900 focus:shadow-sm' : 'bg-white/5 border-white/10 text-white focus:border-white focus:bg-white/[0.07]'}`} data-id="element-3400" data-mp="3hjkrd8su1bb4" data-mpi={i} />)}
              </motion.div>
              {/* Error message */}
              <AnimatePresence data-id="element-3401" data-mp="3o6punnrjhks4">
                {error && <motion.div initial={{
                opacity: 0,
                y: -8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -8
              }} transition={{
                duration: 0.25
              }} className={`flex items-center gap-2 px-4 py-3 rounded-xl ${isLight ? 'bg-red-50 border border-red-100' : 'bg-red-500/10 border border-red-500/20'}`} data-id="element-3402" data-mp="3s6q752v2vlwj">
                    <AlertCircle className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-red-500' : 'text-red-400'}`} data-id="element-3403" data-mp="3blhjgp2xyx88" />
                    <p className={`text-sm font-medium ${isLight ? 'text-red-600' : 'text-red-400'}`} data-id="element-3404" data-mp="36tq0azm47ts4">
                      {error}
                    </p>
                  </motion.div>}
              </AnimatePresence>

              <Button fullWidth size="lg" onClick={handleVerify} loading={isLoading} disabled={otp.some(d => !d)} data-id="element-3405" data-mp="3ujamrshh9965">
                Verify Code
              </Button>

              <div className="text-center space-y-4" data-id="element-3406" data-mp="1v811gutqxvz0">
                <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-3407" data-mp="1v9c8r4mehluv">
                  {deliveryMethod === 'call' ? 'Didn’t receive the call? ' : 'Didn’t receive code? '}
                  {countdown > 0 ? <span className={isLight ? 'text-slate-400' : 'text-zinc-600'} data-id="element-3408" data-mp="2b7egt4axb6c5">
                      {deliveryMethod === 'call' ? `Call again in ${formatCountdown(countdown)}` : `Resend in ${formatCountdown(countdown)}`}
                    </span> : <button onClick={() => setCountdown(120)} className={`font-medium transition-colors ${isLight ? 'text-vexu-600 hover:text-vexu-700' : 'text-vexu hover:text-vexu-400'}`} data-id="element-3409" data-mp="2u2v1rtfsh5wh">
                      {deliveryMethod === 'call' ? 'Call me again' : 'Resend now'}
                    </button>}
                </p>

                {deliveryMethod === 'whatsapp' ? <button type="button" onClick={() => switchDeliveryMethod('call')} className={`inline-flex items-center gap-1.5 text-sm font-semibold transition-colors ${isLight ? 'text-slate-600 hover:text-slate-900' : 'text-zinc-300 hover:text-white'}`} data-id="element-3410" data-mp="3u4i0exvkfi90">
                    <Phone className="w-4 h-4" data-id="element-3411" data-mp="gxgb9p7qapfu" />
                    Having trouble with WhatsApp? Get a code by phone call
                  </button> : <button type="button" onClick={() => switchDeliveryMethod('whatsapp')} className={`text-sm font-medium transition-colors ${isLight ? 'text-vexu-600 hover:text-vexu-700' : 'text-vexu hover:text-vexu-400'}`} data-id="element-3412" data-mp="loy9w6ma4dxn">
                    Use WhatsApp instead
                  </button>}

                <button onClick={onBack} className={`block mx-auto text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-3413" data-mp="axtb6hksn83k">
                  Change phone number
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Loading Overlay */}
      <AnimatePresence data-id="element-3414" data-mp="tpm8upat5hhm">
        {isLoading && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex flex-col items-center justify-center p-8" data-id="element-3415" data-mp="onoilb3zpmf6">
            <div className={`absolute inset-0 ${isLight ? 'bg-white/95' : 'bg-[#0A0A0A]/95'} backdrop-blur-xl`} data-id="element-3416" data-mp="35dvkpfn103g4" />
            <div className="relative z-10 flex flex-col items-center text-center w-full max-w-[280px]" data-id="element-3417" data-mp="1fw83s794flx2">
              <div className="relative w-32 h-32 mb-8" data-id="element-3418" data-mp="1kv1ke8jpft1g">
                <motion.div animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.1, 0.3]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute inset-0 rounded-full border-2 border-vexu/30" data-id="element-3419" data-mp="1kurpq7npsxpf" />
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'linear'
            }} className="absolute inset-4 rounded-full border-2 border-transparent border-t-vexu border-r-vexu/30" data-id="element-3420" data-mp="114zbfqdu3mg8" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-3421" data-mp="mifauoxbjf6i">
                  <motion.div animate={{
                scale: [1, 1.1, 1]
              }} transition={{
                duration: 1.5,
                repeat: Infinity
              }} data-id="element-3422" data-mp="mg1duaj2vswb">
                    <ShieldCheck className="w-10 h-10 text-vexu" data-id="element-3423" data-mp="8icvpageazuy" />
                  </motion.div>
                </div>
              </div>
              <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3424" data-mp="3uyh0qlokddu0">
                Verifying Code
              </p>
              <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3425" data-mp="3lxix44i1f8by">
                Checking your verification code...
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}